import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;
  /** @type {ResizeObserver | null} */
  #headerResizeObserver = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateHeaderHeight);

    if ('ResizeObserver' in window) {
      this.#headerResizeObserver = new ResizeObserver(() => this.#updateHeaderHeight());
    }
    window.addEventListener('resize', this.#updateHeaderHeight, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateHeaderHeight);

    window.removeEventListener('resize', this.#updateHeaderHeight);
    this.#headerResizeObserver?.disconnect();
    this.#headerResizeObserver = null;
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    // Ensure scroll area stays correct if header height changed (e.g., free shipping message).
    requestAnimationFrame(() => this.#updateHeaderHeight());

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateHeaderHeight = () => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    const header = dialog.querySelector('#cart-drawer-header');
    const content = dialog.querySelector('[data-cart-drawer-content]');

    if (!(header instanceof HTMLElement) || !(content instanceof HTMLElement)) return;

    // Keep updating if header height changes while open (e.g., cart rerender / message text changes).
    this.#headerResizeObserver?.disconnect();
    this.#headerResizeObserver?.observe(header);

    const height = Math.ceil(header.getBoundingClientRect().height);
    content.style.setProperty('--header-height', `${height}px`);
  };

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
